import { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import { ApiErrorModalProps, CustomerInformationContainerProps } from '@msc-b2c/interfaces/types';
import { getActiveSession, RootState } from '@msc-b2c/shared-utils';

import { CustomerInformationPopUp } from './components/CustomerInformationPopUp';
import { CustomerInformationSection } from './components/CustomerInformationSection';
import { CustomerInformationSectionInfo } from './components/CustomerInformationSectionInfo';

import { handlePasswordManagementCta } from './helpers/helpers';
import { useGetCustomerContactInformation } from './hooks/hooks';

import { ErrorModal } from '../errormodal/ErrorModal';

import styles from './customerinformationcontainer.module.scss';

export function CustomerInformationContainer({
  dataComponentName,
  passwordSectionTitleIcon = '',
  passwordSectionTitle = '',
  passwordSectionCtaLabel = '',
  passwordSectionCtaLink = { valid: false, url: '' },
  fullLoginAuthenticationExchangePage = '',
  phoneAndEmailSectionTitleIcon = '',
  phoneAndEmailSectionTitle = '',
  phoneAndEmailSectionCtaLabel = '',
  emailLabel = '',
  phoneNumberLabel = '',
  mobileNumberLabel = '',
  addressSectionTitleIcon = '',
  addressSectionTitle = '',
  addressSectionCtaLabel = '',
  addressLabel = '',
  cityLabel = '',
  postalCodeLabel = '',
  verifyZipCodeButtonEnabled = false,
  verifyZipCodeButtonLabel = '',
  stateLabel = '',
  privacySectionTitleIcon = '',
  privacySectionTitle = '',
  privacySectionCtaLabel = '',
  enableCheckboxTitleAndSubtitle = false,
  privacyAgreementText = undefined,
  formPrivacyFields,
  popupPrivacyTitle = '',
  popupPrivacyCloseIcon = '',
  formAddressFields,
  popupAddressTitle = '',
  popupAddressCloseIcon = '',
  formPhoneEmailFields,
  popupPhoneEmailTitle = '',
  popupPhoneEmailCloseIcon = '',
  popupPrivacySubmitButtonLabel = '',
  popupPrivacyCancelButtonLabel = '',
  popupAddressSubmitButtonLabel = '',
  popupAddressCancelButtonLabel = '',
  popupPhoneEmailSubmitButtonLabel = '',
  popupPhoneEmailCancelButtonLabel = '',
  errorCtaLabel = '',
  errorClosingIcon = '',
  errors,
}: Readonly<CustomerInformationContainerProps>): ReactNode {
  const [areUserDataOutdated, setAreUserDataOutdated] = useState<boolean>(true);
  const [modalType, setModalType] = useState<'PRIVACY' | 'ADDRESS' | 'PHONEEMAIL' | ''>('');
  const [error, setError] = useState<ApiErrorModalProps | null>(null);

  const { instance } = useMsal();
  const azureConfig = useSelector(
    (state: RootState) => state?.genericConfigs?.configs?.azureB2CConfig
  );
  const { goldenId } = getActiveSession();
  const customerInfo = useGetCustomerContactInformation(
    areUserDataOutdated,
    setAreUserDataOutdated,
    setError,
    errors,
    goldenId
  );

  function getPopUpContent(modalType: 'PRIVACY' | 'ADDRESS' | 'PHONEEMAIL') {
    const modalContentByType = {
      PRIVACY: {
        iconType: popupPrivacyCloseIcon,
        title: popupPrivacyTitle,
        submitLabel: popupPrivacySubmitButtonLabel,
        cancelLabel: popupPrivacyCancelButtonLabel,
        formElementsPopUp: formPrivacyFields?.childInputs,
      },
      ADDRESS: {
        iconType: popupAddressCloseIcon,
        title: popupAddressTitle,
        submitLabel: popupAddressSubmitButtonLabel,
        cancelLabel: popupAddressCancelButtonLabel,
        verifyZipCodeButtonEnabled: verifyZipCodeButtonEnabled,
        verifyZipCodeButtonLabel: verifyZipCodeButtonLabel,
        formElementsPopUp: formAddressFields?.childInputs,
      },
      PHONEEMAIL: {
        iconType: popupPhoneEmailCloseIcon,
        title: popupPhoneEmailTitle,
        submitLabel: popupPhoneEmailSubmitButtonLabel,
        cancelLabel: popupPhoneEmailCancelButtonLabel,
        formElementsPopUp: formPhoneEmailFields?.childInputs,
      },
    };

    return modalContentByType[modalType];
  }

  return (
    <div
      id={dataComponentName}
      className={styles['cmp-customer-information-container']}
      data-mymsc-account-settings-customer-info=""
    >
      <CustomerInformationSection
        sectionTitleIcon={passwordSectionTitleIcon}
        sectionTitle={passwordSectionTitle}
        sectionCtaLabel={passwordSectionCtaLabel}
        sectionCTAOnClick={() =>
          handlePasswordManagementCta(
            passwordSectionCtaLink,
            azureConfig,
            instance,
            fullLoginAuthenticationExchangePage
          )
        }
      />
      <CustomerInformationSection
        sectionTitleIcon={phoneAndEmailSectionTitleIcon}
        sectionTitle={phoneAndEmailSectionTitle}
        sectionCtaLabel={phoneAndEmailSectionCtaLabel}
        sectionCTAOnClick={() => setModalType('PHONEEMAIL')}
      >
        <CustomerInformationSectionInfo
          infoLabel={emailLabel}
          infoValue={customerInfo?.email?.toLowerCase()}
        />
        <CustomerInformationSectionInfo
          infoLabel={phoneNumberLabel}
          infoValue={customerInfo?.phone}
        />
        <CustomerInformationSectionInfo
          infoLabel={mobileNumberLabel}
          infoValue={customerInfo?.mobilePhone}
        />
      </CustomerInformationSection>
      <CustomerInformationSection
        sectionTitleIcon={addressSectionTitleIcon}
        sectionTitle={addressSectionTitle}
        sectionCtaLabel={addressSectionCtaLabel}
        sectionCTAOnClick={() => setModalType('ADDRESS')}
      >
        <CustomerInformationSectionInfo
          infoLabel={addressLabel}
          infoValue={customerInfo?.address?.addressLine1}
        />
        <CustomerInformationSectionInfo
          infoLabel={cityLabel}
          infoValue={customerInfo?.address?.town}
        />
        <CustomerInformationSectionInfo
          infoLabel={postalCodeLabel}
          infoValue={customerInfo?.address?.postCode}
        />
        <CustomerInformationSectionInfo
          infoLabel={stateLabel}
          infoValue={customerInfo?.address?.state || customerInfo?.address?.region}
        />
      </CustomerInformationSection>
      <CustomerInformationSection
        sectionTitleIcon={privacySectionTitleIcon}
        sectionTitle={privacySectionTitle}
        sectionCtaLabel={privacySectionCtaLabel}
        sectionCTAOnClick={() => setModalType('PRIVACY')}
      />
      {modalType && (
        <CustomerInformationPopUp
          {...getPopUpContent(modalType)}
          id={`${modalType.toLowerCase()}_modal`}
          modalType={modalType}
          customerInfo={customerInfo}
          goldenId={goldenId}
          handleClosePopUp={() => {
            setModalType('');
            document.body.classList.remove('-overlay');
          }}
          setAreUserDataOutdated={setAreUserDataOutdated}
          setError={setError}
          errors={errors}
          enableCheckboxTitleAndSubtitle={enableCheckboxTitleAndSubtitle}
          privacyAgreementText={privacyAgreementText}
        />
      )}
      {(error?.message || error?.title) && (
        <ErrorModal
          title={error.title}
          message={error.message}
          styleOption={error.styleOption}
          iconType={error.iconType}
          closingIcon={errorClosingIcon}
          ctaLabel={errorCtaLabel}
          handleClosure={() => setError(null)}
          handleClick={() => setError(null)}
        />
      )}
    </div>
  );
}
